import { IServiceCategoryDomainValue, ITranslateProps } from 'models/common';
import * as React from 'react';
import * as c from 'models/constants';
import { translate } from 'react-i18next';
import { Field } from 'redux-form';
import { BookingInputField, BookingSelectMenu, BookingTextareaField } from 'components/FormControls';
import { maxLength, required } from 'helpers/validationattributes';
import { IServiceGroup } from 'models/shop/serviceGroups';
import { IServiceForm } from 'models/shop/shopBookingTemplates';

interface IProps extends ITranslateProps {
	shopTypes: number[];
	serviceCategories: IServiceCategoryDomainValue[];
	serviceGroups: IServiceGroup[];
	currentFormValues: IServiceForm;
	change: (field: string, value: any) => void;
}

interface IState {}

const fieldNames = {
	serviceName: 'info.serviceName',
	serviceCategory: 'info.serviceCategory',
	serviceGroup: 'info.serviceGroup',
	serviceDescription: 'info.serviceDescription',
};

const maxLength200 = maxLength(200);
const maxLength500 = maxLength(500);

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class BasicInformation extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);

		this.validateServiceCategory = this.validateServiceCategory.bind(this);
		this.forceRevalidation = this.forceRevalidation.bind(this);

		this.state = {};
	}

	private generateCategoryItems = () => {
		const { shopTypes, serviceCategories, t } = this.props;
		const items = [];

		if (shopTypes?.length && serviceCategories?.length) {
			serviceCategories.forEach((sc) => {
				sc.serviceCategoryShopTypes.some((sc2) => {
					shopTypes.forEach((st) => {
						if (st === sc2) {
							items.push({ ...sc, value: t(`DV_ServiceCategories_${sc.key}`) });
						}
					});
				});
			});
		}

		// Filter duplicates by key
		const filteredItems = items.filter((v, i, a) => a.findIndex((v2) => v2.key === v.key) === i);

		return filteredItems;
	};

	private generateServiceGroupItems = () => {
		const { serviceGroups } = this.props;
		const items = [];

		if (serviceGroups?.length) {
			serviceGroups.forEach((sg) => {
				// Show only top level service groups and filter by their respective service category
				if (
					sg.parentId === null &&
					this.props.currentFormValues?.values?.info?.serviceCategory &&
					sg.serviceCategoryId === this.props.currentFormValues.values.info.serviceCategory
				) {
					items.push({
						...sg,
						key: sg.id,
					});
				}
			});
		}

		return items;
	};

	// Validate if service group is in the currently selected service category, otherwise warn the user
	private validateServiceCategory = () => {
		const { serviceGroups, currentFormValues, t } = this.props;
		const selectedServiceGroup = serviceGroups.find((sg) => sg.id === currentFormValues?.values?.info?.serviceGroup);

		if (
			selectedServiceGroup &&
			selectedServiceGroup?.serviceCategoryId === currentFormValues?.values?.info?.serviceCategory
		) {
			return undefined;
		} else {
			return t('User_ShopCard_Services_BasicInformation_ServiceGroupValidation');
		}
	};

	private forceRevalidation = () => {
		setTimeout(() => {
			this.props.change('_validationHack', Date.now());
		}, 100);
	};

	render() {
		const { t } = this.props;

		return (
			<div className="singleAddEditService__basicInformation">
				<h2 className="singleAddEditService__basicInformation__title">
					{t('User_ShopCard_Services_BasicInformation')}
				</h2>
				<p className="singleAddEditService__basicInformation__subText">
					{t('User_ShopCard_Services_BasicInformation_NameTreatment')}
				</p>
				<div className="singleAddEditService__basicInformation__fieldsContainer">
					<Field
						className="singleAddEditService__basicInformation__serviceName"
						name={fieldNames.serviceName}
						component={BookingInputField}
						type="text"
						label={t('User_ShopCard_Services_ServiceName')}
						information={t('User_ShopCard_Services_ServiceName')}
						validate={[required, maxLength200]}
						placeholder={t('User_ShopCard_Example_Hair_Services')}
					/>
					<Field
						className="singleAddEditService__basicInformation__serviceCategory"
						name={fieldNames.serviceCategory}
						component={BookingSelectMenu}
						type="text"
						items={this.generateCategoryItems()}
						label={t('User_ShopCard_Services_ServiceCategory')}
						information={t('User_ShopCard_Services_ServiceCategory')}
						validate={[required]}
						placeholder={t('User_ShopCard_Services_ChooseCategory')}
						isRequired={true}
						searchable={false}
						onChangeAction={() => this.forceRevalidation()}
					/>
					<Field
						className="singleAddEditService__basicInformation__serviceGroup"
						name={fieldNames.serviceGroup}
						component={BookingSelectMenu}
						type="text"
						items={this.generateServiceGroupItems()}
						label={t('User_ShopCard_Services_ServiceGroup')}
						information={t('User_ShopCard_Services_ServiceGroup')}
						validate={[required, this.validateServiceCategory]}
						placeholder={t('User_ShopCard_Services_ChooseServiceGroup')}
						isRequired={true}
						onChangeAction={() => this.forceRevalidation()}
					/>
					<Field
						className="singleAddEditService__basicInformation__description"
						name={fieldNames.serviceDescription}
						component={BookingTextareaField}
						type="textarea"
						rows={2}
						validate={[maxLength500]}
						maxLength={500}
						information={t('User_ShopCard_Services_ServiceDescription')}
					/>
				</div>
			</div>
		);
	}
}

export { BasicInformation };
